import React, { useEffect, useRef, createContext, useContext, useCallback } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Context to expose Lenis control methods
interface LenisContextType {
  setLocked: (locked: boolean) => void;
  scrollTo: (target: string | number, offset?: number) => void;
}

const LenisContext = createContext<LenisContextType | null>(null);

// Hook to access Lenis controls from anywhere
export const useLenis = () => {
  const context = useContext(LenisContext);
  if (!context) {
    return { setLocked: () => {}, scrollTo: () => {} };
  }
  return context;
};

interface SmoothScrollProps {
  children: React.ReactNode;
}

export const SmoothScroll: React.FC<SmoothScrollProps> = ({ children }) => {
  const lenisRef = useRef<Lenis | null>(null);

  // Initialize Lenis on mount and drive it from GSAP's ticker so
  // ScrollTrigger and Lenis stay perfectly in sync.
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.5,
    });

    lenisRef.current = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    const tick = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, []);

  // Simple stop/start control - no body manipulation
  const setLocked = useCallback((locked: boolean) => {
    if (locked) {
      lenisRef.current?.stop();
    } else {
      lenisRef.current?.start();
    }
  }, []);

  const scrollTo = useCallback((target: string | number, offset = 0) => {
    lenisRef.current?.scrollTo(target, { offset, duration: 1.4 });
  }, []);

  const lenisControls: LenisContextType = {
    setLocked,
    scrollTo,
  };

  return <LenisContext.Provider value={lenisControls}>{children}</LenisContext.Provider>;
};

export default SmoothScroll;
